import type { PresentedEvent, ResolutionResult } from '@core/events';
import type { ChoiceHint } from '@core/content/schema';
import type { StatDelta } from '@core/types';
import './Cards.css';

const HINT_COPY: Record<ChoiceHint, string> = {
  safe: 'Low risk',
  mixed: 'Trade-off',
  risky: 'Could go badly',
  unknown: 'Unclear',
  costly: 'Expensive',
  slow: 'Takes years',
};

const CATEGORY_COPY: Record<string, string> = {
  career: 'Work',
  education: 'Education',
  finance: 'Money',
  investment: 'Investing',
  business: 'Business',
  relationship: 'People',
  romance: 'Relationship',
  family: 'Family',
  health: 'Health',
  wellbeing: 'Wellbeing',
  social: 'People',
  crisis: 'Crisis',
  opportunity: 'Opportunity',
  luck: 'Chance',
  inertia: 'Unfinished',
  leisure: 'Time',
  goal: 'Goal',
  legacy: 'Legacy',
  life: 'Life',
};

interface EventCardProps {
  presented: PresentedEvent;
  onChoose: (choiceId: string) => void;
}

export function EventCard({ presented, onChoose }: EventCardProps) {
  const { event, choices } = presented;
  return (
    <section className="card card--event" aria-labelledby="event-title">
      <div className="card__head">
        <span className="eyebrow">{CATEGORY_COPY[event.category] ?? event.category}</span>
        <h2 id="event-title" className="card__title">
          {event.title}
        </h2>
        <p className="card__body">{event.description}</p>
      </div>

      <ul className="card__choices">
        {choices.map(({ choice, enabled, lockedReason }) => (
          <li key={choice.id}>
            <button
              type="button"
              className={`choice${enabled ? '' : ' choice--locked'}`}
              onClick={() => enabled && onChoose(choice.id)}
              disabled={!enabled}
              aria-describedby={choice.detail ? `detail-${choice.id}` : undefined}
            >
              <span className="choice__main">
                <span className="choice__label">{choice.label}</span>
                {choice.detail && (
                  <span className="choice__detail" id={`detail-${choice.id}`}>
                    {choice.detail}
                  </span>
                )}
                {!enabled && lockedReason && (
                  <span className="choice__locked">Needs {lockedReason}</span>
                )}
              </span>
              {choice.hint && enabled && (
                <span className={`choice__hint choice__hint--${choice.hint}`}>
                  {HINT_COPY[choice.hint]}
                </span>
              )}
              {!enabled && (
                <span className="choice__hint choice__hint--unknown" aria-hidden="true">
                  Locked
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface OutcomeCardProps {
  title: string;
  result: ResolutionResult;
  onContinue: () => void;
}

export function OutcomeCard({ title, result, onContinue }: OutcomeCardProps) {
  return (
    <section className="card card--outcome" aria-labelledby="outcome-title">
      <div className="card__head">
        <span className="eyebrow">{result.deflected ? 'It did not happen' : 'What followed'}</span>
        <h2 id="outcome-title" className="card__title card__title--outcome">
          {result.outcomeLabel}
        </h2>
        <p className="card__body">{result.narrative}</p>
        <p className="card__source">{title}</p>
      </div>

      {result.deltas.length > 0 && <DeltaList deltas={result.deltas} />}

      <button type="button" className="btn btn--primary btn--wide" onClick={onContinue} autoFocus>
        Continue
      </button>
    </section>
  );
}

export function DeltaList({ deltas, limit = 7 }: { deltas: StatDelta[]; limit?: number }) {
  const visible = deltas.slice(0, limit);
  return (
    <ul className="deltas" aria-label="What changed">
      {visible.map((delta) => {
        const change = delta.after - delta.before;
        const isMoney = delta.key === 'cash' || delta.key === 'salary';
        const goodDirection = delta.key === 'stress' || delta.key === 'burnoutLoad' ? change < 0 : change > 0;
        return (
          <li key={delta.key} className={`delta${goodDirection ? ' delta--up' : ' delta--down'}`}>
            <span className="delta__label">{delta.label}</span>
            <span className="delta__value numeral">
              {change > 0 ? '+' : '−'}
              {isMoney
                ? `$${Math.abs(change).toLocaleString('en-US')}`
                : Math.abs(change)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
